import {ChangeDetectorRef, Component, OnInit, ViewEncapsulation} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ToolbarModule} from 'primeng/toolbar';
import {CommonModule} from '@angular/common';
import {RippleModule} from 'primeng/ripple';
import {MenuItem, SharedModule} from 'primeng/api';
import {AvatarModule} from 'primeng/avatar';
import {MenuModule} from 'primeng/menu';
import {AccountService, DataStateService, MyarpaValidationService, StorageService} from '../../service';
import {NavigationEnd, Router, RouterLink, RouterLinkActive} from '@angular/router';
import {UtilsService} from '@shared-lib';
import {NavbarService} from './navbar.service';
import {MenubarModule} from 'primeng/menubar';
import {FunzioniKleros, OwnerType, TipoProfilo, UserDossierModel, UserInfoModel, Wallet} from '../../../shared/models';
import {filter} from 'rxjs/operators';
import {NavbarLinkComponent} from './navbar-link.component';
import {NAVBAR_ITEMS} from '../../../shared/constants/app-constants';
import {Button} from 'primeng/button';
import {Tooltip} from 'primeng/tooltip';
import {Popover} from 'primeng/popover';
import {finalize, Subscription} from 'rxjs';
import {ThemeService} from '../../theme/theme.service';
import {WalletService} from '../../service/kleros/wallet/wallet.service';
import {ProgressSpinner} from 'primeng/progressspinner';
import {DialogsUtilsService} from '../../service/utils/dialogs-utils.service';
import {CreaWalletDialogComponent} from '../../../views/wallet/crea-wallet-dialog/crea-wallet-dialog.component';
import {RicaricaWalletDialogComponent} from '../../../views/wallet/ricarica-wallet-dialog/ricarica-wallet-dialog.component';

@Component({
	selector: 'kleros-navbar',
	templateUrl: 'navbar.component.html',
	imports: [
		CommonModule,
		RippleModule,
		AvatarModule,
		MenuModule,
		ToolbarModule,
		SharedModule,
		TranslateModule,
		MenubarModule,
		NavbarLinkComponent,
		Button,
		Tooltip,
		RouterLink,
		RouterLinkActive,
		Popover,
		ProgressSpinner
	],
	styleUrls: ['navbar.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class NavbarComponent implements OnInit {
	menuUtenteItems: MenuItem[];
	menuConfigurazioneItems: MenuItem[];
	menuDossierItems: MenuItem[];
	langMenu: MenuItem[];

	protected readonly TipoProfilo = TipoProfilo;

	// per check sull'url di routing:
	hideNavItems = true;
	isURLDossierMyarpa = false;

	// per gestione id root MyArpa:
	disableFunctions = false;
	idRootSubscription: Subscription = new Subscription();

	userInfoData: UserInfoModel;

	// per gestire l'icona (up/down) del menu dossier
	menuDossierOpened: boolean = false;

	// per gestire l'icona (up/down) del menu gestione portafoglio
	menuGestionePortafoglioOpened: boolean = false;

	// menù lingua / configurazione / utente aperti: anello sull'avatar (+ freccia su/giù per l'utente)
	menuLangOpened: boolean = false;
	menuDominioOpened: boolean = false;
	menuUtenteOpened: boolean = false;

	// nome della lingua corrente per il tooltip dell'avatar lingua (stesse etichette del menù)
	private static readonly NOMI_LINGUE: Record<string, string> = {it: 'Italiano', en: 'English', de: 'Deutsch'};

	// dati dossier:
	openDossier: {readOnly: boolean; userDossier: UserDossierModel};

	// dati x wallets:
	loadingWallets = true;
	wallets: Wallet[] = [];
	menuGestionePortafoglioItems: MenuItem[] = [];

	subscriptions: Subscription = new Subscription();

	protected readonly FunzioniKleros = FunzioniKleros;
	protected readonly OwnerType = OwnerType;

	constructor(protected translate: TranslateService,
	            private storage: StorageService,
	            protected account: AccountService,
	            protected router: Router,
	            protected utils: UtilsService,
	            protected navbarService: NavbarService,
	            protected state: DataStateService,
	            private themeService: ThemeService,
	            private walletService: WalletService,
	            protected validationService: MyarpaValidationService,
	            protected dialogsUtils: DialogsUtilsService,
							protected cdRef: ChangeDetectorRef) {
	}

	ngOnInit() {
		this.createMenuUtenteDefault();

		this.account.identity().subscribe((userInfo: UserInfoModel) => {
			// gestione notifiche/avvisi navbar (dopo login e solo dopo recupero dati utente)
			this.state.updateNotifiche();
			this.userInfoData = userInfo;
			this.themeService.applyThemeFromAzienda(userInfo.azienda);
			this.state.setUserAzienda(userInfo.azienda?.desinenzaDominio);
			// recupero i wallets dell'utente qui
			this.loadWalletsData();
			this.checkCurrentRouterUrl();
		});

		this.state.activeApp$.subscribe((app) => {
			if (this.state.isMyArpaAppOpen(app)) {
				// con MyArp@ occorre sempre avere un idRoot settato per attivare le funzionalità (altrimenti solo famiglia)
				this.idRootSubscription = this.state.idRoot$.subscribe((idRoot) => (this.disableFunctions = !idRoot));
			} else {
				this.disableFunctions = false;
				this.idRootSubscription?.unsubscribe();
			}
		});

		// gestione ricarica menù in lingua corrente:
		this.translate.onLangChange.subscribe(() => this.createMenuModels());
		// gestione voci navbar in navigazione attiva:
		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => this.checkCurrentRouterUrl());
		// gestione dossier aperto:
		this.state.getOpenDossier$().subscribe((dossier) => (this.openDossier = dossier));
		// gestione aggiornamento saldo wallets:
		this.walletService.updateWalletData.subscribe(() => this.loadWalletsData());
	}

	/**
	 * classe colore saldo wallet: rosso se saldo <= 0, giallo se sotto soglia di allerta, altrimenti nessun colore
	 * (mutuamente esclusive: il rosso ha sempre la precedenza sul giallo)
	 */
	getWalletColorClass(wallet: Wallet): string {
		if (wallet.currentBalance <= 0) {
			return 'text-danger';
		}
		if (wallet.currentBalance < wallet.alertThreshold) {
			return 'text-warning';
		}
		return '';
	}

	private loadWalletsData() {
		this.subscriptions.add(this.walletService.getUsableWallets(this.userInfoData.azienda?.id)
			.pipe(finalize(() => this.loadingWallets = false))
			.subscribe(wallets => {
				this.wallets = wallets;
				this.createMenuModels();
			}));
	}

	private createMenuUtenteDefault() {
		// di default nel menù utente sempre almeno la logout: così in caso di errore "grave" almeno l'utente può sloggarsi
		this.menuUtenteItems = [
			{
				items: [
					{
						label: this.translate.instant('logout'),
						icon: 'mdi mdi-logout',
						command: () => this.account.logout()
					}
				]
			}
		];
	}

	/**
	 * Gestione sola voce "Applicazioni" nella navbar
	 * N.B.: è per non dover sovrascrivere i navItems delle funzionalità app selezionata - gestione avanti/indietro da browser
	 * questo fa si che non venga rieseguita la chiamata alle funzioni collegate all'app selezionata in ogni pagina
	 */
	private checkCurrentRouterUrl() {
		this.state.updateCurrentActiveAppId();
		const currentURL: string = this.router.url;
		this.isURLDossierMyarpa = currentURL.endsWith('/dossier');
		this.hideNavItems =
			this.isURLDossierMyarpa ||
			!this.state.currentActiveAppId ||
			!currentURL.includes('/' + this.state.currentActiveAppId + '/');
		// auto-carico sempre le voci della navbar dallo storage
		if (
			this.utils.checkValid(this.state.currentActiveAppId) &&
			this.storage.hasItemSession(NAVBAR_ITEMS)
		) {
			this.navbarService.setNavbarItemsFromStorage();
		}
	}

	/**
	 * creazione menù di dx della navbar (utente / lang / configurazione)
	 */
	private createMenuModels() {
		this.menuUtenteItems = [
			{
				label: this.translate.instant('menuUtente'),
				items: [
					{
						label: this.translate.instant('profilo'),
						icon: 'mdi mdi-badge-account',
						routerLink: 'user-profile'
					},
					{
						label: this.translate.instant('logout'),
						icon: 'mdi mdi-logout',
						command: () => this.account.logout()
					}
				]
			}
		];

		this.menuConfigurazioneItems = [
			{
				label: this.translate.instant('configurazione'),
				items: [
					{
						label: this.translate.instant('utenti'),
						icon: 'mdi mdi-account-group',
						routerLink: 'configurazione/users'
					},
					{
						label: this.translate.instant('aziende'),
						icon: 'mdi mdi-domain',
						routerLink: 'configurazione/companies'
					}
				]
			}
		];

		if (this.account.isAdmin) {
			// FIXME - non servono...
			/*this.menuConfigurazioneItems[0].items.unshift(
				...[
					{
						label: this.translate.instant('applicazioni'),
						icon: 'mdi mdi-apps',
						routerLink: 'configurazione/apps'
					},
					{
						label: this.translate.instant('funzioni'),
						icon: 'mdi mdi-select-group',
						routerLink: 'configurazione/functions'
					}
				]
			);*/

			// storico licenze: pannello dedicato subito sotto la voce "Utenti"
			this.menuConfigurazioneItems[0].items.splice(1, 0, {
				label: this.translate.instant('logLicenze'),
				icon: 'mdi mdi-license',
				routerLink: 'configurazione/licenze-log'
			});

			// popup x aggiungere notifiche di avviso alla fine prima del logout
			this.menuUtenteItems[0].items.splice(-1, 0, {
				label: this.translate.instant('addNotifica'),
				icon: 'mdi mdi-bell-cog-outline',
				command: () => this.apriPopoverAvviso()
			});
		}

		// menù unico "Gestione portafoglio" in navbar: saldo wallet in cima (solo l'importo colorato), poi titolo e voci
		// (crea/ricarica wallet, transazioni, provvigioni, listino) - lista piatta per evitare l'header in cima al menù
		const gestionePortafoglioItems: MenuItem[] = [];
		if (this.wallets.length) {
			this.wallets.forEach((wallet, $index) => {
				const walletLabel = OwnerType.COMPANY === wallet.ownerType ? this.translate.instant('aziendale') : this.translate.instant('personale');
				const isLast: boolean = $index === this.wallets.length - 1;
        gestionePortafoglioItems.push({
					label: walletLabel + `: <span class="${this.getWalletColorClass(wallet)} font-bold"> ${wallet.currentBalance} €</span>`,
					icon: 'pi pi-wallet',
					escape: false,
					styleClass: 'menuitem-display-only pt-1' + (isLast ? ' pb-1' : '')
				});
			});
			gestionePortafoglioItems.push({separator: true});
		}
		if (this.account.isAdmin) {
      gestionePortafoglioItems.push({
        label: this.translate.instant('gestionePortafoglio'),
        styleClass: 'menuitem-display-only menu-section-label pt-1'
      });
			gestionePortafoglioItems.push({
				label: this.translate.instant('listaPortafogli'),
				icon: 'mdi mdi-wallet-bifold-outline',
				routerLink: 'wallet-admin'
			});
			gestionePortafoglioItems.push({
				label: this.translate.instant('creaWallet'),
				icon: 'mdi mdi-wallet-plus-outline',
				command: () => this.utils.openDialog(CreaWalletDialogComponent, {
					header: this.translate.instant('creaWallet'),
					style: {'min-width': '30%'}
				})
			});
			if (this.wallets.length) {
				gestionePortafoglioItems.push({
					label: this.translate.instant('ricaricaWallet'),
					icon: 'mdi mdi-battery-charging-70',
					command: () => this.utils.openDialog(RicaricaWalletDialogComponent, {
						header: this.translate.instant('ricaricaWallet'),
						style: {'min-width': '30%'}
					})
				});
			}
		}
		if (this.wallets.length) {
			gestionePortafoglioItems.push({
				label: this.translate.instant('transazioni'),
				icon: 'mdi mdi-bank-transfer',
				routerLink: 'wallet-transactions'
			});
			gestionePortafoglioItems.push({
				label: this.translate.instant('provvigioni'),
				icon: 'mdi mdi-cash-multiple',
				routerLink: 'wallet-provvigioni'
			});
		}
		if (this.account.isAdmin) {
			gestionePortafoglioItems.push({
				label: this.translate.instant('listino'),
				icon: 'mdi mdi-format-list-bulleted',
				routerLink: 'wallet-listino'
			});
		}
		this.menuGestionePortafoglioItems = gestionePortafoglioItems;

		// separatore prima di logout
		this.menuUtenteItems[0].items.splice(-1, 0, {separator: true});

		this.menuDossierItems = [
			{
				label: this.translate.instant('documenti'),
				icon: 'mdi mdi-file-document-multiple',
				routerLink: 'applicazioni/myarpadossier/documenti'
			}
		];

		this.langMenu = [
			{
				label: this.translate.instant('lingua'),
				items: [
					{
						icon: 'fi fi-it',
						label: 'Italiano',
						command: () => this.translate.use('it')
					},
					{
						icon: 'fi fi-en',
						label: 'English',
						command: () => this.translate.use('en')
					},
					{
						icon: 'fi fi-de',
						label: 'Deutsch',
						command: () => this.translate.use('de')
					}
				]
			}
		];
	}

	get nomeLinguaCorrente(): string {
		return NavbarComponent.NOMI_LINGUE[this.translate.currentLang] ?? this.translate.currentLang;
	}

	apriPopoverAvviso() {
		return this.dialogsUtils.openDialogNotifica();
	}

}
